import React, { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import useAmpGate from "@/hooks/useAmpGate";

interface Props {
  projectId: string;
  isOwner: boolean;
  /** Rollen som den inloggade personen har i projektet. */
  role?: string;
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });

const fmtDate = (d: string) => new Date(d).toLocaleDateString("sv-SE");

/**
 * Varnar när utförandeskedet används utan gällande arbetsmiljöplan.
 * Endast byggherren (projektägaren) får kvittera ett undantag, och då krävs
 * en utförlig motivering samt ett planerat datum för när planen är på plats.
 */
export const AmpGateBanner: React.FC<Props> = ({ projectId, isOwner, role }) => {
  const {
    loading,
    ampReady,
    planStatus,
    exceptionMotivation,
    exceptionAt,
    exceptionPlannedDate,
    exceptionRole,
    acknowledge,
  } = useAmpGate(projectId);
  const [open, setOpen] = useState(false);
  const [motivation, setMotivation] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [saving, setSaving] = useState(false);

  if (loading || ampReady) return null;

  const today = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (motivation.trim().length < 30) {
      toast.error("Skriv en utförlig motivering på minst 30 tecken");
      return;
    }
    if (!plannedDate) {
      toast.error("Ange planerat datum för när arbetsmiljöplanen är gällande");
      return;
    }
    if (plannedDate < today) {
      toast.error("Det planerade datumet kan inte ligga bakåt i tiden");
      return;
    }
    setSaving(true);
    const ok = await acknowledge(motivation.trim(), plannedDate, role || "Byggherre");
    setSaving(false);
    if (ok) {
      setOpen(false);
      setMotivation("");
      setPlannedDate("");
      toast.success("Undantaget är registrerat och loggat");
    } else {
      toast.error("Kunde inte spara undantaget");
    }
  };

  if (exceptionMotivation) {
    const overdue = exceptionPlannedDate ? exceptionPlannedDate < today : false;
    return (
      <Alert variant={overdue ? "destructive" : "default"}>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>
          {overdue
            ? "Undantaget har passerat sitt datum – arbetsmiljöplanen måste bli gällande nu"
            : "Utförandet pågår med kvitterat undantag"}
        </AlertTitle>
        <AlertDescription className="text-sm">
          Kvitterat av {exceptionRole || "byggherren"}
          {exceptionAt ? ` ${fmt(exceptionAt)}` : ""}
          {exceptionPlannedDate
            ? `. Planen ska vara gällande senast ${fmtDate(exceptionPlannedDate)}`
            : ""}
          : {exceptionMotivation}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Arbetsmiljöplanen är inte gällande</AlertTitle>
        <AlertDescription className="space-y-2 text-sm">
          <p>
            Enligt AFS 2023:3 ska arbetsmiljöplanen finnas tillgänglig innan byggarbetet startar.
            Planen har status{" "}
            <span className="font-medium">{planStatus ? planStatus : "saknas helt"}</span> och är
            inte överlämnad till Bas-U. Utförandedokumenten är därför markerade som ej klara att
            använda.
          </p>
          <div className="rounded-md bg-background/60 p-3 text-xs">
            <p className="font-medium">Så här kommer ni vidare:</p>
            <ol className="mt-1 list-decimal space-y-0.5 pl-4">
              <li>Slutför arbetsmiljöplanen (Dok 12) och sätt den till gällande.</li>
              <li>Gör överlämningen Bas-P → Bas-U (Dok 20) och låt Bas-U kvittera.</li>
              <li>Först därefter öppnas utförandedokumenten utan varning.</li>
            </ol>
          </div>
          {isOwner ? (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              Kvittera undantag med motivering
            </Button>
          ) : (
            <p className="text-xs">
              Endast byggherren (projektägaren) kan kvittera ett undantag.
            </p>
          )}
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kvittera undantag</DialogTitle>
            <DialogDescription>
              Motiveringen sparas med tidsstämpel, roll och vem som kvitterade, och visas i
              projektet fram tills planen är gällande.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivering (minst 30 tecken)</Label>
              <Textarea
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder="Varför startar utförandet utan gällande arbetsmiljöplan, vilka arbeten omfattas och vilka skyddsåtgärder gäller under tiden?"
                rows={4}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {motivation.trim().length}/30 tecken
              </p>
            </div>
            <div>
              <Label>Planerat datum då planen är gällande</Label>
              <Input
                type="date"
                min={today}
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Kvitteras som: {role || "Byggherre"}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? "Sparar..." : "Kvittera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AmpGateBanner;
